import React from "react";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography, Grid, Tooltip } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useParcels } from "../../../services/clear-customs/overall";
import { formatNumberShort } from "../../../components/formatter";

HighchartsMore(Highcharts);


export function ParcelsCleared({ today }) {
  const { parcelsCleared, rateData } = useParcels();

  const createGaugeOptions = (rate) => ({
    chart: {
      type: "gauge",
      height: 100,
      width: 200,
      backgroundColor: "transparent",
      margin: [0, 0, 0, 0],
      spacing: [0, 0, 0, 0]
    },
    title: { text: null },
    pane: {
      center: ["50%", "75%"],
      size: "120%",
      startAngle: -90,
      endAngle: 90,
      background: [
        {
          backgroundColor: "#99bdf3",
          innerRadius: "75%",
          outerRadius: "100%",
          shape: "arc",
          borderWidth: 0
        }
      ]
    },
    yAxis: {
      min: 0,
      max: 100,
      lineWidth: 0,
      tickWidth: 0,
      minorTickWidth: 0,
      tickAmount: 0,
      minorTickInterval: null,
      labels: { enabled: false },
      plotBands: [
        {
          from: 0,
          to: rate || 0,
          color: "#005ae0",
          innerRadius: "75%",
          outerRadius: "100%"
        }
      ]
    },
    series: [
      {
        name: "Rate",
        data: [rate || 0],
        dataLabels: { enabled: false },
        dial: {
          radius: "60%",
          backgroundColor: "var(--font-strong-default)",
          baseWidth: 2,
          baseLength: "0%",
          rearLength: "0%"
        },
        pivot: {
          radius: 3,
          backgroundColor: "#1a1a2e"
        },
        enableMouseTracking: false
      }
    ],
    tooltip: { enabled: false },
    credits: { enabled: false },
    legend: { enabled: false },
    exporting: { enabled: false }
  });

  return (
    <Box sx={{ borderRadius: "8px", overflow: "hidden", boxShadow: "0 2px 4px -2px var(--10), 0 4px 6px -1px var(--10)", border: "solid 1px var(--new-border)", backgroundColor: "var(--app-body)" }}>
      <Box
        sx={{
          height: "48px",
          flexGrow: 0,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
          borderBottom: "solid 1px var(--new-border)"
        }}
      >
        <Typography
          data-testid={`customs_cleared_heading`}
          color="var(--text-primary)" variant="subtitle1"
          fontWeight={600} fontSize="20px"
          sx={{
            height: "28px",
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "8px",
            padding: 0
          }}
        >
          <FormattedMessage id="customsCleared" defaultMessage="Customs Cleared" />
        </Typography>
      </Box>

      {!Array.isArray(parcelsCleared) || parcelsCleared.length === 0 ? (
        <Typography
          style={{ textAlign: "center", padding: "3rem 0" }}
          variant="subtitle1"
        >
          <FormattedMessage id="noData" />
        </Typography>
      ) : (
        <Grid container>
          {parcelsCleared.map((item, index) => {
            const rate = rateData[index]?.clearedSuccessRate || 0;
            const parcels = item?.parcelCount || 0;

            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    mb: 2
                  }}
                >
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={createGaugeOptions(rate)}
                    />
                    <Typography component="span" sx={{
                      position: "absolute",
                      top: "52%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "51px",
                      height: "24px",
                      fontFamily: "Inter",
                      fontSize: "16px",
                      fontWeight: 600,
                      lineHeight: 1.5,
                      letterSpacing: "normal",
                      textAlign: "center",
                      color: "var(--info-innovation-100)",
                      pointerEvents: "none",
                      whiteSpace: "nowrap",
                      display: "block"
                    }}>
                      {(rate || 0).toFixed(1)}%
                    </Typography>
                  </Box>

                  {/* Legend */}
                  <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "12px", mt: "-8px", mb: "12px" }}>
                    {[
                      { color: "#005ae0", label: "Cleared" },
                      { color: "#99bdf3", label: "Not Cleared" }
                    ].map(({ color, label }) => (
                      <Box key={label} sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "4px" }}>
                        <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: "Inter", fontSize: "10px", fontWeight: 600, color: "var(--font-medium-default)", lineHeight: 1 }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ width: "150px", textAlign: "center", fontFamily: "Inter", color: "var(--font-strong-default)" }}>
                    <Tooltip placement="right" title={parcels?.toLocaleString?.("de-DE")}>
                      <Typography sx={{ fontFamily: "Inter", fontSize: "20px", fontWeight: 600, lineHeight: 1.4, letterSpacing: "normal", color: "var(--font-strong-default)" }}>
                        {formatNumberShort(parcels)} <FormattedMessage id="parcels" defaultMessage="Parcels" />
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontFamily: "Inter", fontSize: "16px", fontWeight: 600, lineHeight: 1.5, letterSpacing: "normal", color: "var(--font-strong-default)" }}>
                      <FormattedMessage id="Cleared" defaultMessage="Cleared" />
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
